"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface ResizablePanesProps {
  left: React.ReactNode;
  right: React.ReactNode;
  /** Persist key (e.g. workspace-pane-split). */
  storageKey?: string;
  /** Initial split ratio (0-1). */
  defaultRatio?: number;
  minRatio?: number;
  maxRatio?: number;
}

/**
 * Split horizontal con divider draggable. Sin dependencias externas.
 * - Drag con mouse: actualiza ratio en tiempo real
 * - Doble click en el divider: reset a 50/50
 * - Persiste el ratio en localStorage con la `storageKey`
 */
export function ResizablePanes({
  left,
  right,
  storageKey = "wiki-pane-split",
  defaultRatio = 0.5,
  minRatio = 0.2,
  maxRatio = 0.8,
}: ResizablePanesProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ratio, setRatio] = useState<number>(() => {
    if (typeof window === "undefined") return defaultRatio;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = Number(stored);
      if (Number.isFinite(parsed) && parsed >= minRatio && parsed <= maxRatio) {
        return parsed;
      }
    }
    return defaultRatio;
  });
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const next = Math.min(maxRatio, Math.max(minRatio, x / rect.width));
      setRatio(next);
    };
    const handleUp = () => {
      setDragging(false);
    };
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, [dragging, minRatio, maxRatio]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(storageKey, String(ratio));
    }
  }, [ratio, storageKey]);

  const handleDoubleClick = () => setRatio(defaultRatio);

  return (
    <div ref={containerRef} className="flex-1 flex min-w-0 h-full overflow-hidden">
      <div className="flex min-w-0 h-full" style={{ width: `${ratio * 100}%` }}>
        {left}
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={Math.round(ratio * 100)}
        onMouseDown={() => setDragging(true)}
        onDoubleClick={handleDoubleClick}
        className={cn(
          "w-px bg-border/20 self-stretch shrink-0 relative cursor-col-resize group",
          dragging && "bg-primary/50",
        )}
        title="Arrastra para redimensionar · Doble click para reset"
      >
        <div
          className={cn(
            "absolute inset-y-0 -left-1 -right-1 transition-colors",
            dragging ? "bg-primary/30" : "group-hover:bg-primary/20",
          )}
        />
        {/* Grip indicator */}
        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="w-0.5 h-0.5 rounded-full bg-primary" />
          <span className="w-0.5 h-0.5 rounded-full bg-primary" />
          <span className="w-0.5 h-0.5 rounded-full bg-primary" />
        </div>
      </div>

      <div className="flex-1 flex min-w-0 h-full">
        {right}
      </div>
    </div>
  );
}
